/**
 * 信息步字段唯一性 blur 检查 Hook
 * - 覆盖用户名/邮箱/身份证号/联系电话/备用电话五项唯一性检查
 * - 每项检查持有独立 AbortController，用于取消之前的请求
 * - blur 事件天然单次触发（一个焦点周期仅一次），无需防抖：
 *   防抖的 timer/闭包重建反而是「修改字段值后再次 blur 不触发检查」的时序根源
 * - 每次执行时通过 useRegisterStore.getState() 实时读取表单值与校验动作，
 *   与渲染闭包快照彻底解耦
 */
import { checkUsername, checkEmail, checkIdCard, checkPhone } from '../../../../../api/register';
import { useRegisterStore } from '../../../../../stores/registerStore';
import type { ToastType } from '../types';

export function useDebouncedChecks(
  showToast: (message: string, type?: ToastType) => void,
) {
  // 仅解构 zustand action（引用稳定）；表单值与 validateField 等在检查执行时实时读取
  const {
    setUsernameStatus,
    setEmailStatus,
    setPhoneStatus,
    setBackupPhoneStatus,
    setIdCardStatus,
  } = useRegisterStore();

  // 存储请求控制器，用于取消之前的请求
  const requestControllers = {
    username: null as AbortController | null,
    email: null as AbortController | null,
    phone: null as AbortController | null,
    backupPhone: null as AbortController | null,
    idCard: null as AbortController | null,
  };

  // 检查用户名（blur 直接触发）
  const handleUsernameBlur = async () => {
    const { formData, validateField, setError, clearError } = useRegisterStore.getState();
    if (!formData.username.trim()) return;
    if (!validateField('username')) return;

    if (requestControllers.username) {
      requestControllers.username.abort();
    }
    requestControllers.username = new AbortController();
    setUsernameStatus('checking');

    try {
      const response = await checkUsername(formData.username);
      if (response.status === 'available') {
        setUsernameStatus('ok');
        clearError('username');
      } else if (response.status === 'user') {
        setUsernameStatus('taken');
        setError('username', response.message || '已注册，请登录');
      }
    } catch (error) {
      if ((error as { name?: string }).name !== 'AbortError') {
        setUsernameStatus('idle');
        showToast(error instanceof Error ? error.message : '检查用户名失败', 'error');
      }
    } finally {
      requestControllers.username = null;
    }
  };

  // 检查邮箱（blur 直接触发）
  const handleEmailBlur = async () => {
    const { formData, validateField, setError, clearError } = useRegisterStore.getState();
    if (!formData.email.trim()) return;
    if (!validateField('email')) return;

    if (requestControllers.email) {
      requestControllers.email.abort();
    }
    requestControllers.email = new AbortController();
    setEmailStatus('checking');

    try {
      const response = await checkEmail(formData.email);
      if (response.status === 'available') {
        setEmailStatus('ok');
        clearError('email');
      } else if (response.status === 'customer') {
        setEmailStatus('customer');
        clearError('email');
      } else if (response.status === 'user') {
        setEmailStatus('taken');
        setError('email', response.message || '已注册，请登录');
      }
    } catch (error) {
      if ((error as { name?: string }).name !== 'AbortError') {
        setEmailStatus('idle');
        showToast(error instanceof Error ? error.message : '检查邮箱失败', 'error');
      }
    } finally {
      requestControllers.email = null;
    }
  };

  // 检查身份证号（blur 直接触发）
  const handleIdCardBlur = async () => {
    const { formData, validateField, setError, clearError } = useRegisterStore.getState();
    if (!formData.idCard.trim()) return;
    if (!validateField('idCard')) return;

    if (requestControllers.idCard) {
      requestControllers.idCard.abort();
    }
    requestControllers.idCard = new AbortController();
    setIdCardStatus('checking');

    try {
      const response = await checkIdCard(formData.idCard);
      if (response.status === 'available') {
        setIdCardStatus('ok');
        clearError('idCard');
      } else if (response.status === 'customer') {
        setIdCardStatus('customer');
        clearError('idCard');
      } else if (response.status === 'user') {
        setIdCardStatus('taken');
        setError('idCard', response.message || '已注册，请登录');
      }
    } catch (error) {
      if ((error as { name?: string }).name !== 'AbortError') {
        setIdCardStatus('idle');
        showToast(error instanceof Error ? error.message : '检查身份证号失败', 'error');
      }
    } finally {
      requestControllers.idCard = null;
    }
  };

  // 检查联系电话（blur 直接触发）
  const handlePhoneBlur = async () => {
    const { formData, validateField, setError, clearError } = useRegisterStore.getState();
    if (!formData.phone.trim()) return;
    if (!validateField('phone')) return;

    if (requestControllers.phone) {
      requestControllers.phone.abort();
    }
    requestControllers.phone = new AbortController();
    setPhoneStatus('checking');

    try {
      const response = await checkPhone(formData.phone);
      if (response.status === 'available') {
        setPhoneStatus('ok');
        clearError('phone');
      } else if (response.status === 'customer') {
        setPhoneStatus('customer');
        clearError('phone');
      } else if (response.status === 'user') {
        setPhoneStatus('taken');
        setError('phone', response.message || '已注册，请登录');
      }
    } catch (error) {
      if ((error as { name?: string }).name !== 'AbortError') {
        setPhoneStatus('idle');
        showToast(error instanceof Error ? error.message : '检查电话失败', 'error');
      }
    } finally {
      requestControllers.phone = null;
    }
  };

  // 检查备用电话（blur 直接触发）
  const handleBackupPhoneBlur = async () => {
    const { formData, validateField, setError, clearError } = useRegisterStore.getState();
    if (!formData.backupPhone.trim()) return;
    if (!validateField('backupPhone')) return;

    if (requestControllers.backupPhone) {
      requestControllers.backupPhone.abort();
    }
    requestControllers.backupPhone = new AbortController();
    setBackupPhoneStatus('checking');

    try {
      const response = await checkPhone(formData.backupPhone);
      if (response.status === 'available') {
        setBackupPhoneStatus('ok');
        clearError('backupPhone');
      } else if (response.status === 'customer') {
        setBackupPhoneStatus('customer');
        clearError('backupPhone');
      } else if (response.status === 'user') {
        setBackupPhoneStatus('taken');
        setError('backupPhone', response.message || '已注册，请登录');
      }
    } catch (error) {
      if ((error as { name?: string }).name !== 'AbortError') {
        setBackupPhoneStatus('idle');
        showToast(error instanceof Error ? error.message : '检查备用电话失败', 'error');
      }
    } finally {
      requestControllers.backupPhone = null;
    }
  };

  return {
    handleUsernameBlur,
    handleEmailBlur,
    handlePhoneBlur,
    handleBackupPhoneBlur,
    handleIdCardBlur,
  };
}
